import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import EmptyState from "../components/EmptyState";
import ZoneBadge from "../components/ZoneBadge";
import ConfidenceBadge from "../components/ConfidenceBadge";
import { Filter, X, Eye } from "lucide-react";
import { formatEur, formatNumber, formatPct, formatDate } from "../lib/format";
import { Link } from "react-router-dom";
import { Skeleton } from "../components/ui/skeleton";

const SORT_OPTIONS = [
    { value: "flip_score", label: "Highest Flip Score" },
    { value: "roi", label: "Highest expected ROI" },
    { value: "price_per_sqm", label: "Lowest € / m²" },
    { value: "first_seen", label: "Newest listings" },
    { value: "price_drop", label: "Recent price drops" },
];

const DealFinder = () => {
    const [searchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState(() => ({
        municipality: searchParams.get("municipality") || "",
        sea_view_only: searchParams.get("sea_view_only") === "true",
        min_flip_score: searchParams.get("min_flip_score") || "",
        max_price: "",
        max_price_per_sqm: "",
        property_type: searchParams.get("property_type") || "any",
        zone_class: searchParams.get("zone_class") || "any",
        only_price_reductions: searchParams.get("only_price_reductions") === "true",
        sort_by: "flip_score",
    }));

    const params = useMemo(() => {
        const p = { sort_by: filters.sort_by, limit: 1000 };
        if (filters.municipality) p.municipality = filters.municipality;
        if (filters.sea_view_only) p.sea_view_only = true;
        if (filters.min_flip_score) p.min_flip_score = filters.min_flip_score;
        if (filters.max_price) p.max_price = filters.max_price;
        if (filters.max_price_per_sqm) p.max_price_per_sqm = filters.max_price_per_sqm;
        if (filters.property_type && filters.property_type !== "any") p.property_type = filters.property_type;
        if (filters.zone_class && filters.zone_class !== "any") p.zone_class = filters.zone_class;
        if (filters.only_price_reductions) p.only_price_reductions = true;
        return p;
    }, [filters]);

    useEffect(() => {
        setLoading(true);
        api.get("/listings", { params })
            .then(({ data }) => setItems(data))
            .finally(() => setLoading(false));
    }, [params]);

    const update = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
    const reset = () =>
        setFilters({
            municipality: "",
            sea_view_only: false,
            min_flip_score: "",
            max_price: "",
            max_price_per_sqm: "",
            property_type: "any",
            zone_class: "any",
            only_price_reductions: false,
            sort_by: "flip_score",
        });

    return (
        <div className="space-y-6" data-testid="deal-finder-page">
            <header>
                <h1 className="font-grotesk text-2xl md:text-3xl font-semibold">Deal Finder</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Filter, score, en sorteer alle gesourcde listings. Resultaten zijn leeg
                    totdat scrapers echte data leveren.
                </p>
            </header>

            <Card className="p-5" data-testid="filters-card">
                <div className="flex items-center gap-2 mb-4">
                    <Filter className="w-4 h-4 text-muted-foreground" />
                    <h2 className="font-grotesk font-semibold text-sm">Filters</h2>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={reset}
                        className="ml-auto h-7 text-xs gap-1"
                        data-testid="reset-filters"
                    >
                        <X className="w-3 h-3" /> Reset
                    </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Field label="Municipality">
                        <Input
                            placeholder="Tivat, Kotor, Budva…"
                            value={filters.municipality}
                            onChange={(e) => update("municipality", e.target.value)}
                            data-testid="filter-municipality"
                        />
                    </Field>
                    <Field label="Min flip score">
                        <Input
                            type="number"
                            placeholder="e.g. 70"
                            value={filters.min_flip_score}
                            onChange={(e) => update("min_flip_score", e.target.value)}
                            data-testid="filter-min-flip"
                        />
                    </Field>
                    <Field label="Max price (€)">
                        <Input
                            type="number"
                            placeholder="500000"
                            value={filters.max_price}
                            onChange={(e) => update("max_price", e.target.value)}
                            data-testid="filter-max-price"
                        />
                    </Field>
                    <Field label="Max € / m²">
                        <Input
                            type="number"
                            placeholder="3500"
                            value={filters.max_price_per_sqm}
                            onChange={(e) => update("max_price_per_sqm", e.target.value)}
                            data-testid="filter-max-per-sqm"
                        />
                    </Field>
                    <Field label="Property type">
                        <Select
                            value={filters.property_type}
                            onValueChange={(v) => update("property_type", v)}
                        >
                            <SelectTrigger data-testid="filter-property-type">
                                <SelectValue placeholder="Any" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="any">Any</SelectItem>
                                <SelectItem value="apartment">Apartment</SelectItem>
                                <SelectItem value="villa">Villa</SelectItem>
                                <SelectItem value="townhouse">Townhouse</SelectItem>
                                <SelectItem value="ruin">Ruin</SelectItem>
                                <SelectItem value="old_house">Old house</SelectItem>
                                <SelectItem value="penthouse">Penthouse</SelectItem>
                                <SelectItem value="land">Land</SelectItem>
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Zone">
                        <Select
                            value={filters.zone_class}
                            onValueChange={(v) => update("zone_class", v)}
                        >
                            <SelectTrigger data-testid="filter-zone">
                                <SelectValue placeholder="Any" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="any">Any</SelectItem>
                                <SelectItem value="preferred">Preferred</SelectItem>
                                <SelectItem value="speculative">Speculative</SelectItem>
                                <SelectItem value="avoid">Avoid</SelectItem>
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Sort by">
                        <Select value={filters.sort_by} onValueChange={(v) => update("sort_by", v)}>
                            <SelectTrigger data-testid="filter-sort">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {SORT_OPTIONS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>
                                        {o.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </Field>
                    <div className="flex flex-col gap-3 justify-end pb-1">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox
                                checked={filters.sea_view_only}
                                onCheckedChange={(v) => update("sea_view_only", !!v)}
                                data-testid="filter-sea-view"
                            />
                            Sea view only
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox
                                checked={filters.only_price_reductions}
                                onCheckedChange={(v) => update("only_price_reductions", !!v)}
                                data-testid="filter-price-reductions"
                            />
                            Price reductions only
                        </label>
                    </div>
                </div>
            </Card>

            <Card className="overflow-hidden" data-testid="deals-table-card">
                {loading ? (
                    <div className="p-6 space-y-3">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <Skeleton key={i} className="h-10 w-full" />
                        ))}
                    </div>
                ) : items.length === 0 ? (
                    <EmptyState
                        title="Geen listings die voldoen aan de filters"
                        description={
                            "Er is nog geen echte data in de database. Activeer een scrape-bron en draai een scan; tot die tijd blijft deze tabel leeg conform de real-data discipline."
                        }
                        testid="deals-empty"
                    />
                ) : (
                    <Table className="table-dense">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Listing</TableHead>
                                <TableHead>Zone</TableHead>
                                <TableHead className="text-right">Asking</TableHead>
                                <TableHead className="text-right">€ / m²</TableHead>
                                <TableHead className="text-right">m²</TableHead>
                                <TableHead>Sea view</TableHead>
                                <TableHead className="text-right">Flip score</TableHead>
                                <TableHead className="text-right">ROI</TableHead>
                                <TableHead>Verification</TableHead>
                                <TableHead>First seen</TableHead>
                                <TableHead></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.map((l) => (
                                <TableRow key={l.id} data-testid={`deal-row-${l.id}`}>
                                    <TableCell>
                                        <div className="font-grotesk font-medium text-sm">
                                            {l.title || "Untitled"}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            {l.municipality}
                                            {l.neighbourhood ? ` · ${l.neighbourhood}` : ""}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <ZoneBadge zone={l.zone_class} />
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {formatEur(l.asking_price_eur)}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {l.price_per_sqm_eur ? formatEur(l.price_per_sqm_eur) : "—"}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {l.living_area_sqm ? formatNumber(l.living_area_sqm) : "—"}
                                    </TableCell>
                                    <TableCell className="text-xs capitalize">
                                        {l.sea_view === "unknown" ? "—" : l.sea_view}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums font-semibold">
                                        {l.flip_score !== null && l.flip_score !== undefined
                                            ? formatNumber(l.flip_score)
                                            : "—"}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {l.expected_roi_pct !== null && l.expected_roi_pct !== undefined
                                            ? formatPct(l.expected_roi_pct)
                                            : "—"}
                                    </TableCell>
                                    <TableCell>
                                        <ConfidenceBadge
                                            status={l.provenance?.verification_status}
                                        />
                                    </TableCell>
                                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                                        {formatDate(l.first_seen_at)}
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            asChild
                                            variant="ghost"
                                            size="sm"
                                            data-testid={`open-deal-${l.id}`}
                                            className="h-7 gap-1"
                                        >
                                            <Link to={`/deals/${l.id}`}>
                                                <Eye className="w-3 h-3" />
                                                Open
                                            </Link>
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Card>
        </div>
    );
};

const Field = ({ label, children }) => (
    <div className="space-y-1.5">
        <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium">
            {label}
        </Label>
        {children}
    </div>
);

export default DealFinder;
